import classNames from 'classnames'
import sharedStyles from '../../Settings.module.css'
import { getIcon } from '../../../../common/components/icons/utils'
import styles from './OrganizationInfo.module.css'
import { useTranslation } from 'react-i18next'
import { dateTimeToDashDateString, parseDDMMYYYYcomma, safeIsNullOrEmpty } from '../../../../util/string'
import { NewBusinessInfo } from '../../../../types/commonTypes'
import { LicenceCountButton } from '../../../GameEditor/components/AddPerson/components/LicenceCountButton'
import { useBusinessSettings } from '../../../../contexts/BusinessSettingsContext'
import { useEffect } from 'react'

type OrganizationLicenceInfoProps = {
  name: string
  membersCount?: number
  licenceEndDate: string
  maxMembersCount: number | null
  businessInfo: NewBusinessInfo | null
  limitMembers: boolean
}

export const OrganizationLicenceInfo: React.FC<OrganizationLicenceInfoProps> = ({
  name,
  membersCount,
  licenceEndDate,
  maxMembersCount,
  businessInfo,
  limitMembers,
}) => {
  const { t } = useTranslation()
  const { updateQuota, quota } = useBusinessSettings()

  useEffect(() => {
    updateQuota()
    const interval = setInterval(() => {
      updateQuota()
    }, 30000)

    return () => clearInterval(interval)
  }, [updateQuota])

  return (
    <div className={classNames(sharedStyles.sectionCardContainer, styles.container)}>
      <div className={styles.organizationIconContainer}>
        <span>{getIcon('organizationGradient')}</span>
      </div>
      <div className={styles.orgInfoList}>
        <h1 style={{ maxWidth: '450px' }}>{name}</h1>
        <div className={styles.orgInfoListPart}>
          {membersCount != null && (
            <span className='bold normal'>
              {!maxMembersCount || !limitMembers
                ? t('settings_organization.org_info.members_count', {
                    count: membersCount || 0,
                    defaultValue: '%{count} members',
                  })
                : t('settings_organization.org_info.members_count_with_max', {
                    members_count: membersCount || 0,
                    max_count: maxMembersCount,
                    defaultValue: '%{members_count}/%{max_count} members',
                  })}
            </span>
          )}
          {!safeIsNullOrEmpty(licenceEndDate) && (
            <span className='normal'>
              {t('settings_organization.org_info.license_valid_until', {
                next_invoice: dateTimeToDashDateString(parseDDMMYYYYcomma(licenceEndDate)),
                defaultValue: 'License valid until: %{next_invoice}',
              })}
            </span>
          )}
        </div>
        {quota?.totalSeats && (
          <div className={styles.orgInfoListPart}>
            <span className='bold normal'>
              {t('settings_organization.org_info.players_count', {
                players_count: quota.usedSeats || 0,
                max_count: quota.totalSeats,
                defaultValue: '%{players_count}/%{max_count} players',
              })}
            </span>

            {!safeIsNullOrEmpty(businessInfo?.nextDateWhenLicencesFreed) ? (
              <span className='normal'>
                {t('settings_organization.org_info.licenses_renewed', {
                  next_invoice: businessInfo?.nextDateWhenLicencesFreed
                    ? dateTimeToDashDateString(parseDDMMYYYYcomma(businessInfo?.nextDateWhenLicencesFreed))
                    : '00:00:00',
                  defaultValue: 'Licences renewed: %{next_invoice}',
                })}
              </span>
            ) : (
              <span className='normal'>
                {t('settings_organization.org_info.licenses_valid', {
                  next_invoice: businessInfo?.nextDateWhenLicencesFreed || licenceEndDate,
                  defaultValue: 'Licences valid until: %{next_invoice}',
                })}
              </span>
            )}
            <div className={styles.licenceButton}>
              <LicenceCountButton type='capital' />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
